import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Submission-mode result entry. Used when match.scoring_type === "submission"
 * (or when the tournament default is "submission" and no override exists).
 *
 * Honours scoring_mode:
 *   - self: submit moves match to `pending_validation`; opponent confirms.
 *   - referee_only: submit finalises immediately.
 */
export default function SubmitResultDialog({ open, onOpenChange, match, onSubmitted }) {
  const [p1Frames, setP1Frames] = useState(0);
  const [p2Frames, setP2Frames] = useState(0);
  const [p1HighBreak, setP1HighBreak] = useState("");
  const [p2HighBreak, setP2HighBreak] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setP1Frames(match?.player1_score || 0);
      setP2Frames(match?.player2_score || 0);
      setP1HighBreak("");
      setP2HighBreak("");
      setNotes("");
    }
  }, [open, match]);

  if (!match) return null;

  const bestOf = match.best_of || 5;
  const winning = Math.floor(bestOf / 2) + 1;
  const p1Wins = p1Frames >= winning;
  const p2Wins = p2Frames >= winning;
  const tie = p1Frames === p2Frames && p1Frames + p2Frames > 0;
  const valid = (p1Wins || p2Wins) && !tie;

  const submit = async () => {
    if (!valid) {
      toast.error(`Best-of-${bestOf}: one player must win ${winning} frames, no ties`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        player1_frames: p1Frames,
        player2_frames: p2Frames,
        player1_highest_break: p1HighBreak ? parseInt(p1HighBreak) : null,
        player2_highest_break: p2HighBreak ? parseInt(p2HighBreak) : null,
        notes: notes.trim() || null,
      };
      const res = await axios.post(`${API}/matches/${match.id}/submit-result`, payload, { withCredentials: true });
      toast.success(res.data.needs_validation ? "Submitted — waiting for opponent confirmation" : "Result recorded");
      onSubmitted?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="submit-result-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-primary" weight="fill" />
            Submit Match Result
          </DialogTitle>
          <DialogDescription>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="border-primary/40 text-primary">
                Manual Submission
              </Badge>
              <Badge variant="outline" className="text-muted-foreground">
                Best of {bestOf}
              </Badge>
            </div>
            <span className="block mt-1">
              <strong>{match.player1_name || "P1"}</strong> vs <strong>{match.player2_name || "P2"}</strong>
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{match.player1_name || "Player 1"} frames</Label>
              <Input
                type="number" min="0" max={bestOf}
                value={p1Frames}
                onChange={e => setP1Frames(parseInt(e.target.value) || 0)}
                data-testid="p1-frames"
              />
            </div>
            <div>
              <Label className="text-xs">{match.player2_name || "Player 2"} frames</Label>
              <Input
                type="number" min="0" max={bestOf}
                value={p2Frames}
                onChange={e => setP2Frames(parseInt(e.target.value) || 0)}
                data-testid="p2-frames"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{match.player1_name || "P1"} highest break (optional)</Label>
              <Input type="number" min="0" max="147"
                value={p1HighBreak}
                onChange={e => setP1HighBreak(e.target.value)}
                data-testid="p1-highbreak" />
            </div>
            <div>
              <Label className="text-xs">{match.player2_name || "P2"} highest break (optional)</Label>
              <Input type="number" min="0" max="147"
                value={p2HighBreak}
                onChange={e => setP2HighBreak(e.target.value)}
                data-testid="p2-highbreak" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value.slice(0, 300))}
              placeholder="Referee comments, weather, anything unusual…"
              rows={2}
              data-testid="match-notes"
            />
            <p className="text-[10px] text-muted-foreground text-right">{notes.length}/300</p>
          </div>

          <div className={`p-2 rounded text-xs border ${valid ? "border-chart-3/30 bg-chart-3/5 text-chart-3" : "border-muted-foreground/20 bg-muted/20 text-muted-foreground"}`}>
            {tie
              ? "Frames cannot be tied."
              : valid
                ? `${p1Wins ? match.player1_name : match.player2_name} wins ${Math.max(p1Frames, p2Frames)}-${Math.min(p1Frames, p2Frames)}`
                : `Need ${winning} frames to win (best of ${bestOf})`}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || submitting} data-testid="submit-result-btn">
            <PaperPlaneTilt className="w-4 h-4 mr-1" weight="fill" />
            {submitting ? "Submitting…" : "Submit Result"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
